import { SlideIn } from '@atlaskit/motion';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import usePanelState from '@databases/panel/usePanelState';
import { getAllPanelsForPanelRenderer } from '@databases/panel-types';
import React, {
  type FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { type ImperativePanelHandle, Panel } from 'react-resizable-panels';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import PanelRenderer from './PanelRenderer.tsx';
import OverlayResizeHandle from './overlay-resize-handle.tsx';
import PanelSizes from './panel/sizes.ts';
import { createPanelStorageKey, getDefaultPanelSize } from './panel/utils.ts';
import PanelResizeHandle from './panel-resize-handle.tsx';
import Z_INDEX from './z-index.ts';

const SidebarPanel = styled(Panel)`
  z-index: ${Z_INDEX.SIDE_PANEL_CONTAINER};
  min-width: ${PanelSizes.sidebar.overlayWidth}px;
`;

const overlayContainerStyles = xcss({
  position: 'fixed',
  top: 'space.0',
  right: 'space.0',
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'visible',
  backgroundColor: 'elevation.surface',
  boxShadow: 'elevation.shadow.overlay',
  transition: 'transform 180ms ease-in-out'
});

const componentStyles = xcss({
  width: '100%',
  height: '100%',
  overflow: 'auto'
});

const overlayContentStyles = xcss({
  overflow: 'auto',
  flex: '1',
  minWidth: 'size.0'
});

interface SidePanelProps {
  sidebar?: React.ReactNode | null;
  resizeDisabled?: boolean;
  overlayMode?: boolean;
  isSidePanelVisible?: boolean;
  contentId: string;
}

const SidePanel: FunctionComponent<SidePanelProps> = ({
  sidebar,
  resizeDisabled,
  overlayMode,
  isSidePanelVisible = false,
  contentId
}) => {
  const { getPanelKey } = usePanelState();
  const panelKey = getPanelKey();
  const isEntryViewPanel = panelKey === 'EntryViewPanel';
  const isTimelineLayoutEnabled = useMemo(
    () =>
      getExperimentValue('databasesRoadmap', 'isEnabled', {
        fireExperimentExposure: false
      }) === true,
    []
  );
  const shouldAnimateEntryViewPanel = isEntryViewPanel && isTimelineLayoutEnabled;
  const storageKey = useMemo(() => createPanelStorageKey(panelKey), [panelKey]);
  const analyticsClient = useAnalytics();

  const panelRef = useRef<ImperativePanelHandle>(null);

  const handleDragging = useCallback(
    (isDragging: boolean) => {
      if (!isDragging && panelKey && panelRef.current) {
        const panelSize = panelRef.current.getSize();
        analyticsClient.sendPanelResizedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          panelKey,
          panelSize
        });
      }
    },
    [analyticsClient, contentId, panelKey]
  );

  /**
   * If a sidebar component is passed, that takes priority.
   * If a panel key is set, this defers to the panel component.
   */
  const sidebarComponent = useMemo(() => {
    if (sidebar) {
      return sidebar;
    }

    if (panelKey) {
      const panels = getAllPanelsForPanelRenderer();

      return <PanelRenderer panels={panels} panel={panelKey} />;
    }

    return null;
  }, [panelKey, sidebar]);

  const sidebarPanelProps = useMemo(() => {
    const isDevtools = panelKey === 'DevtoolsPanel';
    const panelType = isDevtools ? 'devtools' : 'sidebar';
    const panelConfig = PanelSizes[panelType];

    return {
      order: panelConfig.order,
      maxSize: panelConfig.max,
      minSize: panelConfig.min,
      defaultSize: getDefaultPanelSize(panelType, storageKey),
      'data-testid': 'layout-right-side-panel'
    };
  }, [storageKey, panelKey]);

  const resizeHandleStyle = useMemo(
    () => ({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      margin: 0
    }),
    []
  );

  const [overlayWidth, setOverlayWidth] = useState(PanelSizes.sidebar.overlayWidth);

  if (overlayMode) {
    return (
      <Box
        xcss={overlayContainerStyles}
        testId="layout-right-side-panel"
        style={{
          zIndex: Z_INDEX.SIDE_PANEL_CONTAINER,
          width: `${overlayWidth}px`,
          transform: isSidePanelVisible
            ? 'translateX(0)'
            : `translateX(calc(100% + ${token('space.200', '16px')}))`,
          pointerEvents: isSidePanelVisible ? 'auto' : 'none'
        }}
      >
        <OverlayResizeHandle
          id="app-overlay-resize-handle"
          style={resizeHandleStyle}
          currentWidth={overlayWidth}
          minWidth={PanelSizes.sidebar.overlayMinWidth}
          maxWidth={PanelSizes.sidebar.overlayMaxWidth}
          onResize={setOverlayWidth}
        />
        <Flex
          justifyContent="start"
          alignItems="center"
          direction="column"
          xcss={[componentStyles, overlayContentStyles]}
        >
          {sidebarComponent}
        </Flex>
      </Box>
    );
  }

  if (!sidebarComponent) {
    return null;
  }

  const panelContent = (
    <Flex
      justifyContent="start"
      alignItems="center"
      direction="column"
      xcss={componentStyles}
    >
      {sidebarComponent}
    </Flex>
  );

  return (
    <>
      <PanelResizeHandle
        id="app-resize-handle"
        style={resizeHandleStyle}
        disabled={resizeDisabled}
        onDragging={handleDragging}
        panelRef={panelRef}
        minSize={sidebarPanelProps.minSize}
        maxSize={sidebarPanelProps.maxSize}
      />

      <SidebarPanel {...sidebarPanelProps} id="right-side-panel" ref={panelRef}>
        {shouldAnimateEntryViewPanel ? (
          <SlideIn
            enterFrom="right"
            fade="in"
            duration="small"
            animationTimingFunction="ease-in-out"
          >
            {(motionProps) => (
              <div
                {...motionProps}
                style={{
                  ...motionProps.style,
                  height: '100%',
                  width: '100%'
                }}
              >
                {panelContent}
              </div>
            )}
          </SlideIn>
        ) : (
          panelContent
        )}
      </SidebarPanel>
    </>
  );
};

export default SidePanel;
